import { useMediaQuery } from "@mui/material";
import "animate.css";
import { waterfallSaleAssets } from "assets/cdn";
import lpIcon from "assets/loyalty-icon-single-v2.png";
import Button from "components/Buttons/ThemeButton";
import DebugCopyIdButton from "debug/components/DebugCopyIdButton";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import useRewardsById from "hooks/useRewardsById";
import { WsDialogs } from "interfaces";
import { FeatureType } from "interfaces/feature-tracker";
import { useDispatch } from "react-redux";
import { featureTrackerAPI } from "redux/apis";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { sortBundleRewards } from "helpers/rewardSort";
import { getTimeLeft } from "utils/time";
import { useImagePath } from "hooks/useImagePath";
import {
	Container,
	Description,
	DescriptionContainer,
	ItemImage,
	Items,
	LockIcon,
	RewardAmount,
	RewardItem
} from "./styles";

export interface ITierStatus {
	index: number;
	completed: boolean;
	tier: {
		rewardId: string;
		imageUrl: string;
		title: string;
		offerId: string;
	};
}

export interface IOffer {
	rewardId: string;
	id: string;
	price: number;
	loyaltyPointReward: Date;
}
interface Props {
	saleId: string;
	tierStatuses: ITierStatus[];
	tierStatus: ITierStatus;
	offer: {
		rewardId: string;
		id: string;
		price: number;
		loyaltyPointReward: Date;
	};
	inputMockChecked?: boolean;
	endTime?: string;
}

export const WaterFallCardV2: React.FC<Props> = ({
	saleId,
	tierStatuses,
	tierStatus,
	offer,
	endTime
}) => {
	const { wsStoreBuy, authToken, suppressOrderConfirmation, postUserMetadata } =
		useWebStoreContext();
	const { getImage } = useImagePath("cdn");
	const checkIcon = getImage(waterfallSaleAssets.checkIcon);
	const chain = getImage(waterfallSaleAssets.chain);
	const isPortrait = useMediaQuery("(max-width:733px)");
	const { nowTime } = useWebStoreContext();
	const { openDialog } = useDialogModalRedux();
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const displayRewards = useRewardsById(
		offer?.rewardId || tierStatus?.tier?.rewardId
	);
	const dispatch = useDispatch();
	const debugId = offer
		? tierStatus?.tier?.offerId
		: tierStatus?.tier?.rewardId;
	const debugLabel = offer ? "OfferID" : "RewardID";

	const isPreviousTierCompleted = (reverseValue = 1) => {
		return tierStatuses[tierStatus?.index - reverseValue]
			? tierStatuses[tierStatus?.index - reverseValue]?.completed
			: true;
	};

	const onClickBuy = async () => {
		if (!authToken?.value) {
			return;
		}
		const invoice = await wsStoreBuy({
			offerId: offer.id,
			waterfallSaleId: saleId,
			waterfallSaleTierIndex: tierStatus?.index
		});

		if (invoice) {
			const timestampMs = new Date().getTime();
			openDialog(WsDialogs.XSOLLA_PURCHASE, {
				xsollaToken: invoice.xsollaToken,
				xsollaHost: invoice.xsollaHost,
				invoice,
				section: "shop",
				source: "waterfall",
				pricePoint: offer?.price,
				suppressOrderConfirmation,
				postUserMetadata,
				onPurchaseStarted: (data) => {
					const minutesLeft = getTimeLeft(nowTime, endTime);
					reportEvent("purchase_started", {
						section_name: "limited_deals",
						offer_id: `${saleId}-${tierStatus?.index}`,
						price_in_cents: offer?.price * 100,
						reward_id: "123",
						invoice_id: invoice?.id,
						paystation_id: data?.payStation
					});
					reportPlaygamiEvent("tp.web_ga.purchase_flow", {
						event_name: "purchase_started",
						event_type: "web_paystation_purchase",
						action: "purchase_started",
						section_name: "limited_deals",
						purchase_flow_id: data?.payStation,
						session_engaged: authToken?.value ? "1" : "0",
						price_in_cents: Math.round(invoice.price * 100),
						page_location: window.location.href,
						engagement_time_msec: (
							Number(data?.data?.milliseconds) - timestampMs
						).toString(),
						reward_id: invoice?.rewardId,
						offer_id: invoice?.offerId,
						ignore_referrer:
							process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
						minutes_left: minutesLeft,
						page_title: location.pathname.replace("/", ""),
						page_referrer: invoice?.invoiceMetadata?.referer,
						invoice_id: invoice?.id
					});
				},
				onPurchaseSuccess: (data) => {
					// Start - Optimistic update for waterfall sale
					dispatch(
						featureTrackerAPI.util.updateQueryData(
							"getFeatureTracker",
							{
								FeatureType: "waterfall-sale",
								isAuthenticated: !!authToken?.value
							},
							(draft) => {
								const { sessionTracker } = draft;
								const tier = sessionTracker.tierStatuses.find(
									(t: ITierStatus) => t.completed === false
								);
								const optimistSessionTracker = {
									...sessionTracker,
									tierStatuses: sessionTracker.tierStatuses.map(
										(t: ITierStatus) =>
											t.index === tier?.index ||
											(t.index === tier?.index + 1 && !!t.tier.rewardId)
												? { ...t, completed: true }
												: t
									)
								};
								return {
									...draft,
									sessionTracker: optimistSessionTracker
								};
							}
						)
					);
					// End - Optimistic update for waterfall sale
					dispatch(
						featureTrackerAPI.util.invalidateTags([
							{
								type: "featureTrackerTag",
								id: FeatureType.WATERFALL_SALE
							}
						])
					);

					const minutesLeft = getTimeLeft(nowTime, endTime);
					reportEvent("purchase_success", {
						invoice_id: invoice?.id
					});
					reportPlaygamiEvent("tp.web_ga.purchase_flow", {
						event_name: "purchase_success",
						event_type: "web_paystation_purchase",
						action: "purchase_success",
						section_name: "limited_deals",
						purchase_flow_id: data?.version,
						session_engaged: authToken?.value ? "1" : "0",
						price_in_cents: Math.round(invoice.price * 100),
						page_location: window.location.href,
						engagement_time_msec: (
							Number(data?.milliseconds) - timestampMs
						).toString(),
						reward_id: invoice?.rewardId,
						offer_id: invoice?.offerId,
						ignore_referrer:
							process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
						minutes_left: minutesLeft,
						page_title: location.pathname.replace("/", ""),
						page_referrer: invoice?.invoiceMetadata?.referer,
						invoice_id: invoice?.id,
						transaction_id: data?.purchase_invoice_id?.toString()
					});
				}
			});
		}
	};

	return (
		<Container image={tierStatus?.tier?.imageUrl}>
			<DescriptionContainer>
				{tierStatus?.index === 0 ? (
					/* Adding paint-order here because safari doesn't support it when switching to landscape mode */
					<Description style={{ paintOrder: "stroke fill" }}>
						{tierStatus?.tier?.title}
					</Description>
				) : (
					<Description
						style={{ paintOrder: "stroke fill" }}
						variant={tierStatus?.tier?.rewardId ? "freeItem" : "item"}
					>
						{tierStatus?.tier?.title}
					</Description>
				)}
			</DescriptionContainer>
			{tierStatus?.completed ? (
				<Items isOffer={false}>
					<img
						className="animate__animated animate__rubberBand"
						src={checkIcon}
						width={isPortrait ? "55%" : "80%"}
					/>
				</Items>
			) : (
				<>
					<Items isOffer={tierStatus?.tier?.rewardId ? false : true}>
						{sortBundleRewards(displayRewards).map((displayReward) => (
							<RewardItem
								key={displayReward.id}
								filter={
									isPreviousTierCompleted() ||
									(isPreviousTierCompleted(2) && tierStatus?.tier?.rewardId) ||
									(tierStatuses[tierStatus?.index - 1]?.tier?.rewardId &&
										tierStatus?.tier?.rewardId)
										? 0
										: 100 //Disabled
								}
							>
								<ItemImage
									className="item-icon"
									src={displayReward.imageUrl}
									alt=""
								/>
								<RewardAmount
									className="description"
									style={{ paintOrder: "stroke fill" }}
								>
									{displayReward.description &&
										new Intl.NumberFormat("en-US", {
											notation: "compact",
											compactDisplay: "short",
											maximumFractionDigits: 1,
											roundingMode: "floor"
										}).format(
											Number(displayReward.description?.replaceAll(",", ""))
										)}
								</RewardAmount>
							</RewardItem>
						))}
						{offer?.loyaltyPointReward && (
							<RewardItem
								key="lp"
								filter={
									isPreviousTierCompleted() ||
									(isPreviousTierCompleted(2) && tierStatus?.tier?.rewardId) ||
									(tierStatuses[tierStatus?.index - 1]?.tier?.rewardId &&
										tierStatus?.tier?.rewardId)
										? 0
										: 100 //Disabled
								}
							>
								<ItemImage
									className="item-icon"
									src={lpIcon}
									alt="loyalty points bubble icon"
								/>
								<RewardAmount
									className="description"
									style={{ paintOrder: "stroke fill" }}
								>
									{offer?.loyaltyPointReward?.toLocaleString("en-US")}
								</RewardAmount>
							</RewardItem>
						)}
					</Items>
					{offer && (
						<>
							<Button
								variant="contained"
								color="success"
								size="large"
								disabled={!isPreviousTierCompleted()}
								onClick={onClickBuy}
								sx={{
									position: "absolute",
									bottom: "-8%",
									fontSize: "1.5em",
									minWidth: "10.53em",
									height: "2.33em",
									borderRadius: "0.8em"
								}}
							>
								{offer?.price} USD
								{!isPreviousTierCompleted() && <LockIcon src={chain} />}
							</Button>
						</>
					)}
				</>
			)}
			{debugId && (
				<DebugCopyIdButton
					id={debugId}
					label={debugLabel}
					side="left"
					vertical="top"
					style={{ fontSize: "1.2em", zIndex: 1000, left: "0" }}
				/>
			)}
		</Container>
	);
};
